import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  type CreateCarrierOperatorVars,
  useCarrierDispatchCenters,
  useCreateCarrierOperator,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { hasHttpStatus } from "@/lib/api-error";

interface CreateOperatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateOperatorDialog({
  open,
  onOpenChange,
}: CreateOperatorDialogProps) {
  const dispatchCenters = useCarrierDispatchCenters();
  const create = useCreateCarrierOperator();
  const submissionInFlight = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [form, setForm] = useState<CreateCarrierOperatorVars>({
    dispatchCenterId: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
  });
  const [licenseNumber, setLicenseNumber] = useState("");
  const [scheduleNotes, setScheduleNotes] = useState("");

  const centers = useMemo(() => {
    return (dispatchCenters.data ?? [])
      .filter((center) => center.isActive)
      .map((center) => ({ id: center.id, label: center.name }));
  }, [dispatchCenters.data]);

  useEffect(() => {
    if (open && !form.dispatchCenterId && centers[0]) {
      setForm((current) => ({
        ...current,
        dispatchCenterId: centers[0].id,
      }));
    }
  }, [centers, form.dispatchCenterId, open]);

  const reset = () => {
    setForm({
      dispatchCenterId: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dateOfBirth: "",
    });
    setLicenseNumber("");
    setScheduleNotes("");
    setSubmitError(null);
  };

  const updateField = (
    field: keyof CreateCarrierOperatorVars,
    value: string
  ) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const canSubmit =
    !!form.dispatchCenterId &&
    !!form.firstName.trim() &&
    !!form.lastName.trim() &&
    !!form.email.trim() &&
    !!form.dateOfBirth;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submissionInFlight.current) return;
    submissionInFlight.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: CreateCarrierOperatorVars = {
        ...form,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone?.trim() || undefined,
      };
      if (licenseNumber.trim()) {
        payload.documents = { licenseNumber: licenseNumber.trim() };
      }
      if (scheduleNotes.trim()) {
        payload.workSchedule = { notes: scheduleNotes.trim() };
      }
      await create.mutateAsync(payload);
      toast.success("Operator invitation sent.");
      reset();
      onOpenChange(false);
    } catch (error) {
      const response = error as { response?: { data?: { error?: string } } };
      setSubmitError(
        hasHttpStatus(error, 403)
          ? "You do not have permission to invite operators."
          : (response.response?.data?.error ??
              "Failed to invite operator. Try again.")
      );
    } finally {
      submissionInFlight.current = false;
      setSubmitting(false);
    }
  };

  const pending = create.isPending || submitting;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && pending) return;
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Invite operator"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label
              htmlFor="operator-first-name"
              className="block text-sm font-medium mb-1"
            >
              First name
            </label>
            <Input
              id="operator-first-name"
              value={form.firstName}
              onChange={(e) => updateField("firstName", e.target.value)}
              disabled={pending}
              required
            />
          </div>

          <div>
            <label
              htmlFor="operator-last-name"
              className="block text-sm font-medium mb-1"
            >
              Last name
            </label>
            <Input
              id="operator-last-name"
              value={form.lastName}
              onChange={(e) => updateField("lastName", e.target.value)}
              disabled={pending}
              required
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="operator-email"
            className="block text-sm font-medium mb-1"
          >
            Email
          </label>
          <Input
            id="operator-email"
            type="email"
            value={form.email}
            onChange={(e) => updateField("email", e.target.value)}
            disabled={pending}
            required
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label
              htmlFor="operator-phone"
              className="block text-sm font-medium mb-1"
            >
              Phone
            </label>
            <Input
              id="operator-phone"
              value={form.phone ?? ""}
              onChange={(e) => updateField("phone", e.target.value)}
              disabled={pending}
            />
          </div>

          <div>
            <label
              htmlFor="operator-dob"
              className="block text-sm font-medium mb-1"
            >
              Date of birth
            </label>
            <Input
              id="operator-dob"
              type="date"
              value={form.dateOfBirth}
              onChange={(e) => updateField("dateOfBirth", e.target.value)}
              disabled={pending}
              required
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="operator-center"
            className="block text-sm font-medium mb-1"
          >
            Dispatch center
          </label>
          <NativeSelect
            id="operator-center"
            value={form.dispatchCenterId}
            onChange={(event) =>
              updateField("dispatchCenterId", event.target.value)
            }
            disabled={
              pending || dispatchCenters.isLoading || centers.length === 0
            }
            required
          >
            {centers.length === 0 && <option value="">No centers</option>}
            {centers.map((center) => (
              <option key={center.id} value={center.id}>
                {center.label}
              </option>
            ))}
          </NativeSelect>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label
              htmlFor="operator-license"
              className="block text-sm font-medium mb-1"
            >
              License number
            </label>
            <Input
              id="operator-license"
              value={licenseNumber}
              onChange={(e) => setLicenseNumber(e.target.value)}
              disabled={pending}
            />
          </div>

          <div>
            <label
              htmlFor="operator-schedule-notes"
              className="block text-sm font-medium mb-1"
            >
              Schedule notes
            </label>
            <Input
              id="operator-schedule-notes"
              value={scheduleNotes}
              onChange={(e) => setScheduleNotes(e.target.value)}
              disabled={pending}
            />
          </div>
        </div>

        {dispatchCenters.isLoading && (
          <p role="status" className="text-sm text-muted-foreground">
            Loading dispatch centers...
          </p>
        )}

        {dispatchCenters.error && (
          <p role="alert" className="text-sm text-destructive">
            Failed to load dispatch centers.
          </p>
        )}

        {(submitError || create.isError) && (
          <p
            role="alert"
            aria-label="Operator invitation failed"
            className="text-sm text-destructive"
          >
            {submitError ?? "Failed to invite operator. Try again."}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="touch"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button type="submit" size="touch" disabled={!canSubmit || pending}>
            {pending ? "Sending..." : "Send invite"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
